import React, { useEffect, useMemo, useState } from "react";

import _ from "lodash";

import { PlainTooltip } from "@similarweb/ui-components";
import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { BulletLegends } from "app/components/React/Legends/BulletLegends";

import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { NoPrintContainer, WeShare } from "app/components/weshare/weshare";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import {
    GraphContainer,
    NoDataContainer,
    SitesChartLoaderContainer,
    StyledHeaderTitle,
    TitleContainer,
    UtilitiesContainer,
} from "pro-features/pages/segments/components/benchmarkOvertime/StyledComponents";
import {
    getBarChartConfig,
    multipleDomainsTooltipFormatter,
} from "app/pages/website-analysis/audience-loyalty/chartConfig";
import LoyaltyApiService from "app/pages/website-analysis/audience-loyalty/LoyaltyApiService";
import {
    BoxContainer,
    ButtonsContainer,
    ChartContainer,
    ContentContainer,
    Header,
    TitleRow,
} from "app/pages/website-analysis/audience-loyalty/StyledComponents";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PdfExportService } from "app/services/PdfExportService";
import { allTrackers } from "app/services/track/track";
import { FlexColumn, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

interface IAudienceLoyaltyOverview {
    [key: string]: any;
    isSingleMode?: boolean;
    websitesArrayWithColor?: any;
    chosenSitesLegends?: any;
    comparisonData?: any;
    isLoadingComparisonData?: boolean;
}
export const AudienceLoyalty = (props: IAudienceLoyaltyOverview) => {
    const [isLoading, setIsLoading] = useState(true);
    const [loyaltySitesResponse, setLoyaltySitesResponse] = useState<any>();
    const [loyaltyColumnChartData, setLoyaltyColumnChartData] = useState<any>();
    const { services } = useMemo(() => {
        return {
            services: {
                loyaltyApiService: new LoyaltyApiService(),
                swNavigator: proNavigator,
            },
        };
    }, []);

    const domains = useMemo(() => {
        if (props.isSingleMode) {
            return props.websitesArrayWithColor?.map((website) => {
                return website.name;
            });
        } else {
            return services.swNavigator.getParams().key.split(",");
        }
    }, [props.isSingleMode, props.websitesArrayWithColor]);

    const { chosenSitesLegends } = props;

    const renderComparisonLegend = useMemo(() => {
        let legendsItems = [];
        if (props.isSingleMode) {
            legendsItems = props.websitesArrayWithColor?.map((website, idx) => {
                return {
                    key: idx,
                    name: website.Domain,
                    color: website.color,
                    setOpacity: website.isSuggested,
                };
            });
        } else if (!props.isLoadingComparisonData) {
            legendsItems = props.chosenSitesLegends.map((site, idx) => {
                return {
                    key: idx,
                    name: site.name,
                    color: site.color,
                };
            });
        }
        return <BulletLegends legendItems={legendsItems} />;
    }, [props.websitesArrayWithColor, props.chosenSitesLegends, props.isSingleMode, props.isLoadingComparisonData]);

    function prepareBarChartData({ domains }): any {
        const transformSitesData = _.map(domains, (domain) => {
            return {
                name: domain,
                id: domain,
            };
        });
        return {
            data: [...transformSitesData],
        };
    }

    const comparisonColumnChartData = () => {
        if (props.isLoadingComparisonData) {
            return [];
        }
        const rawData = Object.entries(props.comparisonData);
        const sitesIndex = domains.reduce((result, name, index) => ({ ...result, [name]: index }), {});
        const sortedRawData = rawData.sort((entryA, entryB) =>
            sitesIndex[entryA[0]] > sitesIndex[entryB[0]] ? 1 : -1,
        );
        return sortedRawData.map(([site, data], index) => {
            const siteColor = props.isSingleMode
                ? props.websitesArrayWithColor?.find((website) => website.name === site)?.color
                : props.chosenSitesLegends?.find((website) => website.name === site)?.color;

            return {
                name: site,
                id: site,
                data: Object.entries(data).map(([bucket, val]) => {
                    return {
                        key: bucket,
                        color: siteColor || CHART_COLORS.chartMainColors[index],
                        isSite: true,
                        opacity: props.websitesArrayWithColor?.find((website) => website.name === site)?.isSuggested
                            ? 0.4
                            : 1,
                        y: val.AvgPercentageUsers,
                        dataLabels: {
                            enabled: true,
                        },
                    };
                }),
            };
        });
    };

    const renderColumnChart = () => {
        if (Object.values(props.comparisonData)?.length === 0) {
            return (
                <NoDataContainer>
                    <TableNoData
                        icon="no-data"
                        messageTitle={i18nFilter()("global.nodata.notavilable")}
                    />
                </NoDataContainer>
            );
        }
        const graphData = comparisonColumnChartData();
        return (
            <ChartContainer>
                <Chart
                    type={"column"}
                    config={getBarChartConfig({
                        filter: [percentageSignFilter, 1],
                        tooltipFormatter: multipleDomainsTooltipFormatter,
                    })}
                    data={graphData}
                />
            </ChartContainer>
        );
    };

    useEffect(() => {
        if (!isLoading) {
            setLoyaltyColumnChartData(
                prepareBarChartData({
                    domains,
                }),
            );
        }
    }, [domains]);

    useEffect(() => {
        if (!props.isSingleMode || domains.length > 0) {
            (async () => {
                setIsLoading(true);
                const params: any = services.swNavigator.getParams();
                const apiParams: any = services.swNavigator.getApiParams(params);

                try {
                    const loyaltySitesResponse = await services.loyaltyApiService.getDomainsLoyalty({
                        apiParams,
                        ...(props.isSingleMode
                            ? {
                                  keys: domains.toString(),
                              }
                            : null),
                    });
                    setLoyaltySitesResponse(loyaltySitesResponse);
                } catch {
                    setLoyaltySitesResponse(undefined);
                } finally {
                    setIsLoading(false);
                }
            })();
        }
    }, [props.websitesArrayWithColor, props.isSingleMode, domains]);

    const chartRef = React.useRef<HTMLElement>(null);

    const getPNG = React.useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent(
            "website.analysis.audience.loyalty.graph.download.png",
            "submit-ok",
            "website.analysis.audience.loyalty.graph.download.png",
        );
        const offSetX = 0;
        const offSetY = 50;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current.outerHTML,
            "AudienceLoyalty",
            chartRef.current.offsetWidth + offSetX,
            chartRef.current.offsetHeight + offSetY,
        );
    }, []);

    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
            </ButtonsContainer>
        );
    };

    return (
        <BoxContainer
            data-automation-sites-vs-category={true}
            ref={chartRef}
        >
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <TitleRow>
                            <Header>{i18nFilter()("audience.loyalty.graph.title")}</Header>
                            <PlainTooltip
                                placement="top"
                                tooltipContent={i18nFilter()("audience.loyalty.graph.title.tooltip")}
                            >
                                <span>
                                    <InfoIcon iconName="info" />
                                </span>
                            </PlainTooltip>
                        </TitleRow>
                    </StyledHeaderTitle>
                </FlexColumn>
                <NoPrintContainer>
                    <RightFlexRow>
                        {getUtilityButtons()}
                        <WeShare
                            chartRef={chartRef.current}
                            title={i18nFilter()("audience.loyalty.graph.title")}
                            description={i18nFilter()("audience.loyalty.graph.title")}
                            redirectUrl={`https://www.similarweb.com/website/${chosenSitesLegends[0].displayName}/#overview`}
                        />
                    </RightFlexRow>
                </NoPrintContainer>
            </TitleContainer>
            <ContentContainer>
                {isLoading || props.isLoadingComparisonData ? (
                    <SitesChartLoaderContainer>
                        <GraphLoader width={"100%"} />
                    </SitesChartLoaderContainer>
                ) : (
                    <GraphContainer className={"sharedTooltip"}>
                        {props.comparisonData ? (
                            <>
                                <UtilitiesContainer>{renderComparisonLegend}</UtilitiesContainer>
                                {renderColumnChart()}
                            </>
                        ) : (
                            <NoDataLandscape
                                title={"website.analysis.loyalty.no.data.title"}
                                subtitle={"website.analysis.loyalty.no.data.subtitle"}
                            />
                        )}
                    </GraphContainer>
                )}
            </ContentContainer>
        </BoxContainer>
    );
};
